import { Component, OnInit } from '@angular/core';
import { AbstractControl, ControlContainer, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { DEFAULT_MAXIMUM_VALUE, DEFAULT_MINIMUM_VALUE, DEFAULT_NO_DATA_VALUE, getUnitBasedOnUnitType } from '@models/mood/helpers/dataset';
import { DatasetMetadata, UNIT_TYPE_ENUM } from '@models/mood/metadata';

const numberInputRegex = /^(\+|\-)?(([0-9]+(\.[0-9]*)?)|(\.[0-9]+))((e|E)(\+|\-)[0-9]*(\.[0-9]*)?)?$/;
const formulaInputRegex = /(?:data)/;


export interface RasterLayerConfigurationForm {
  minimum?: FormControl<number | null>;
  maximum?: FormControl<number | null>;
  no_data?: FormControl<number | null>;
  formula?: FormControl<string | null>;
  unit_type?: FormControl<string | null>;
  unit?: FormControl<string | null>;
}

export const minMaxValidator: ValidatorFn = (
  form: AbstractControl<FormGroup<RasterLayerConfigurationForm>>
): ValidationErrors | null => {
  const minimumValue = parseFloat(form.get('minimum')?.value!) || DEFAULT_MINIMUM_VALUE;
  const maximumValue = parseFloat(form.get('maximum')?.value!) || DEFAULT_MAXIMUM_VALUE;

  const error = minimumValue >= maximumValue ? { minMax: true } : null;

  assignErrorsToFormControl(form, 'minimum', 'minMax', error !== null);
  assignErrorsToFormControl(form, 'maximum', 'minMax', error !== null);

  return error;
};

export const noDataValidator: ValidatorFn = (
  form: AbstractControl<FormGroup<RasterLayerConfigurationForm>>
): ValidationErrors | null => {
  const minimumValue = parseFloat(form.get('minimum')?.value!) || DEFAULT_MINIMUM_VALUE;
  const maximumValue = parseFloat(form.get('maximum')?.value!) || DEFAULT_MAXIMUM_VALUE;
  const noDataValue = parseFloat(form.get('no_data')?.value!) || DEFAULT_NO_DATA_VALUE;

  const error = noDataValue >= minimumValue && maximumValue >= noDataValue ? { noData: true } : null;

  assignErrorsToFormControl(form, 'minimum', 'noData', error !== null);
  assignErrorsToFormControl(form, 'maximum', 'noData', error !== null);
  assignErrorsToFormControl(form, 'no_data', 'noData', error !== null);

  return error;
};


function assignErrorsToFormControl(form: AbstractControl, fieldName: string, errorName: string, setError: boolean) {
  const existingControlErrors = form.get(fieldName)?.errors || {};

  if (!setError) {
    delete existingControlErrors[errorName];
  } else {
    existingControlErrors[errorName] = true;
  }

  if (Object.keys(existingControlErrors).length === 0) {
    form.get(fieldName)?.setErrors(null);
  } else {
    form.get(fieldName)?.setErrors(existingControlErrors);
  }
}

interface SelectableUnitType {
  unit_type: UNIT_TYPE_ENUM,
  label: string
}


const CUSTOM_UNIT_TYPE_VALUE = "CUSTOM";

@Component({
  selector: 'mood-admin-raster-layer-form',
  templateUrl: './raster-layer-form.component.html',
  styleUrls: [
    'raster-layer-form.component.scss'
  ]
})
export class RasterLayerFormComponent implements OnInit {
  constructor(private _controlContainer: ControlContainer) { }



  possible_unit_types: SelectableUnitType[] = [];


  unitTypeControl = new FormControl<string>("");

  fieldInvalid(fieldname: string) {
    return this.formGroupContainer.get(fieldname)?.invalid;
  }

  fieldEmpty(fieldname: string) {
    return !this.formGroupContainer.get(fieldname)?.value;
  }

  public get customUnitValue() {
    return CUSTOM_UNIT_TYPE_VALUE;
  }

  ngOnInit(): void {
    const types = Object.values(UNIT_TYPE_ENUM);
    types.forEach(type => {
      if(type === UNIT_TYPE_ENUM.CUSTOM) return;

      const entry: SelectableUnitType = {
        unit_type: type,
        label: type
      }

      const resultingUnit = getUnitBasedOnUnitType(type);
      if (resultingUnit) {
        entry.label += ` (${resultingUnit})`
      }
      this.possible_unit_types.push(entry);
    });

    this.formGroupContainer.valueChanges.subscribe(config => {
      const unitTypeToSet = config.unit_type || CUSTOM_UNIT_TYPE_VALUE;
      this.unitTypeControl.patchValue(unitTypeToSet, { emitEvent: false });
      this.updateformBasedOnUnitType(unitTypeToSet, false);
    });
  }

  private updateformBasedOnUnitType(unit_type: string | null, emit: boolean = true) {
    if (unit_type == CUSTOM_UNIT_TYPE_VALUE) {
      this.formGroupContainer.get('unit')?.enable({ emitEvent: false });
    } else {
      this.formGroupContainer.get('unit')?.reset(null, { emitEvent: false });
      this.formGroupContainer.get('unit')?.disable({ emitEvent: false });
    }
  }

  public unitTypeSelected(event: MatSelectChange) {
    this.updateformBasedOnUnitType(event.value);
    if (event.value == CUSTOM_UNIT_TYPE_VALUE) {
      event.value = "";
    }

    this.formGroupContainer.get('unit_type')!.patchValue(event.value, { emitEvent: false });
    this.formGroupContainer.markAsDirty();
  }

  getFieldErrorMessage(fieldname: string) {
    const fieldErrorKeys = Object.keys(this.formGroupContainer.get(fieldname)?.errors || {});

    if (fieldErrorKeys.includes('pattern')) {
      return "A numeric input is expected.";
    } else if (fieldErrorKeys.includes('minMax')) {
      return "Minimum value can not be higher than maximum value.";
    } else if (fieldErrorKeys.includes('noData')) {
      return "No Data value cannot fall between minimum and maximum values.";
    }
    return;
  }

  get formGroupContainer() {
    return this._controlContainer.control as FormGroup<RasterLayerConfigurationForm>;
  }

  public static createMetadataFormGroup(existingConfig?: DatasetMetadata) {
    const formGroup = new FormGroup<RasterLayerConfigurationForm>({
      minimum: new FormControl<number | null>(existingConfig?.minimum || null, [Validators.pattern(numberInputRegex)]),
      maximum: new FormControl<number | null>(existingConfig?.maximum || null, [Validators.pattern(numberInputRegex)]),
      no_data: new FormControl<number | null>(existingConfig?.no_data || null, [Validators.pattern(numberInputRegex)]),
      formula: new FormControl<string | null>(existingConfig?.formula || null, [Validators.pattern(formulaInputRegex)]),
      unit_type: new FormControl<string | null>(existingConfig?.unit_type || null),
      unit: new FormControl<string | null>(existingConfig?.unit || null)
    });//
    // TODO: [minMaxValidator, noDataValidator]); Add these validators back, the problem is that grouped files do not use the 'upper' defined min/max/nodata, but use the default fallback values. This results in some cases not being able to save changes

    return formGroup;
  };
}
